import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useUser } from '@/hooks/useUser';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as yup from 'yup';

const profileSchema = yup.object({
  name: yup.string().required('Name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
});

type ProfileFormValues = yup.InferType<typeof profileSchema>;

export const AccountProfilePage = () => {
  const { data: user, updateUser, requestAccountDeletion } = useUser();
  const [isRequestingDeletion, setIsRequestingDeletion] = useState(false);

  const form = useForm<ProfileFormValues>({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
    },
  });

  const onSubmit = async (data: ProfileFormValues) => {
    try {
      await updateUser.mutateAsync({ name: data.name });
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleDeleteRequest = async () => {
    if (
      !confirm(
        'Are you sure you want to request account deletion? This cannot be undone.',
      )
    ) {
      return;
    }

    setIsRequestingDeletion(true);
    try {
      await requestAccountDeletion.mutateAsync();
      toast.success(
        'Delete request sent successfully. Our team will contact you shortly.',
      );
    } catch (error) {
      toast.error('Failed to request account deletion');
    } finally {
      setIsRequestingDeletion(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input {...field} type="email" disabled />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-between items-center">
          <Button type="submit" isLoading={updateUser.isPending}>
            Save Changes
          </Button>

          <Button
            type="button"
            variant="destructive"
            isLoading={isRequestingDeletion}
            onClick={handleDeleteRequest}>
            Request Account Deletion
          </Button>
        </div>
      </form>
    </Form>
  );
};
